import { Course, UserRole, Module, Assignment, Announcement, Material, Grade } from '../types/lms.types';
export const getMockModules = (): Module[] => [
  { id: 'all', name: 'All Modules', description: 'View all content', order: 0 },
  { id: 'mod1', name: 'Module 1: Introduction to Algebra', description: 'Basic algebraic concepts and fundamentals', order: 1 },
  { id: 'mod2', name: 'Module 2: Linear Equations', description: 'Solving linear equations and systems', order: 2 },
  { id: 'mod3', name: 'Module 3: Quadratic Functions', description: 'Quadratic equations and parabolas', order: 3 },
  { id: 'mod4', name: 'Module 4: Advanced Topics', description: 'Complex mathematical concepts and applications', order: 4 }
];

export const getMockAssignments = (): Assignment[] => [
  {
    id: 1,
    title: 'Linear Algebra Problem Set',
    description: 'Complete exercises 1-15 from Chapter 3. Show all working steps.',
    moduleId: 'mod1',
    dueDate: '20/1/2024',
    status: 'Pending',
    submissions: '32/45',
    icon: '📚',
    maxGrade: 100
  },
  {
    id: 2,
    title: 'Video Presentation - Mathematical Concepts',
    description: 'Create a 5-minute video explaining a mathematical concept of your choice.',
    moduleId: 'mod2',
    dueDate: '15/1/2024',
    status: 'Submitted',
    submissions: '45/45',
    icon: '🎥',
    maxGrade: 50
  },
  {
    id: 3,
    title: 'Quadratic Equations Quiz',
    description: 'Online quiz covering quadratic formula and graphing.',
    moduleId: 'mod3',
    dueDate: '25/1/2024',
    status: 'Pending',
    submissions: '28/45',
    icon: '📊',
    maxGrade: 75
  },
  {
    id: 4,
    title: 'Algebraic Expressions Worksheet',
    description: 'Practice worksheet with 20 algebraic expressions to simplify.',
    moduleId: 'mod1',
    dueDate: '18/1/2024',
    status: 'Graded',
    submissions: '45/45',
    icon: '📝',
    maxGrade: 30
  }
];

export const getMockAnnouncements = (): Announcement[] => [
  {
    id: 1,
    title: 'Welcome to Advanced Mathematics',
    content: 'Welcome everyone! This semester we will cover advanced mathematical concepts. Please ensure you have the required textbook.',
    moduleId: 'all',
    date: '5/1/2024',
    author: 'Dr. Sarah Johnson',
    priority: 'high'
  },
  {
    id: 2,
    title: 'Module 1 Resources Available',
    content: 'All resources for Introduction to Algebra are now available in the Materials section.',
    moduleId: 'mod1',
    date: '8/1/2024',
    author: 'Dr. Sarah Johnson',
    priority: 'medium'
  },
  {
    id: 3,
    title: 'Office Hours This Week',
    content: 'Office hours will be extended this week. Tuesday and Thursday 2-4 PM in Room 205.',
    moduleId: 'all',
    date: '12/1/2024',
    author: 'Dr. Sarah Johnson',
    priority: 'medium'
  }
];

export const getMockMaterials = (): Material[] => [
  {
    id: 1,
    name: 'Lecture Notes - Chapter 1',
    type: 'PDF',
    size: '2.4 MB',
    date: '2 days ago',
    moduleId: 'mod1'
  },
  {
    id: 2,
    name: 'Video Tutorial - Basics',
    type: 'Video',
    size: '45.2 MB',
    date: '1 week ago',
    moduleId: 'mod1'
  },
  {
    id: 3,
    name: 'Linear Equations Examples',
    type: 'PDF',
    size: '1.8 MB',
    date: '3 days ago',
    moduleId: 'mod2'
  },
  {
    id: 4,
    name: 'Quadratic Functions Guide',
    type: 'PDF',
    size: '3.2 MB',
    date: '1 day ago',
    moduleId: 'mod3'
  }
];

export const getMockGrades = (): Grade[] => [
  {
    id: 1,
    studentName: 'Alice Johnson',
    assignmentTitle: 'Linear Algebra Problem Set',
    moduleId: 'mod1',
    score: 85,
    maxScore: 100,
    status: 'Graded',
    submittedDate: '18/1/2024'
  },
  {
    id: 2,
    studentName: 'Bob Smith',
    assignmentTitle: 'Video Presentation',
    moduleId: 'mod2',
    score: 42,
    maxScore: 50,
    status: 'Graded',
    submittedDate: '14/1/2024'
  },
  {
    id: 3,
    studentName: 'Carol Davis',
    assignmentTitle: 'Algebraic Expressions Worksheet',
    moduleId: 'mod1',
    score: 28,
    maxScore: 30,
    status: 'Graded',
    submittedDate: '17/1/2024'
  }
];

export const getMockCourses = (userRole: UserRole): Course[] => {
  const baseCourses = [
    {
      id: '1',
      name: 'Advanced Mathematics',
      teacher: 'Dr. Sarah Johnson',
      teacherId: 'teacher1',
      students: 45,
      isLive: true,
      progress: 78,
      assignments: 3,
      announcements: 2,
      description: 'Advanced mathematical concepts and applications',
      semester: 'Fall 2024',
      credits: 4
    },
    {
      id: '2',
      name: 'Computer Science Fundamentals',
      teacher: 'Prof. Michael Chen',
      teacherId: 'teacher2',
      students: 38,
      isLive: false,
      progress: 65,
      assignments: 2,
      announcements: 1,
      description: 'Introduction to computer science principles',
      semester: 'Fall 2024',
      credits: 3
    },
    {
      id: '3',
      name: 'Physics Laboratory',
      teacher: 'Dr. Emily Watson',
      teacherId: 'teacher3',
      students: 32,
      isLive: false,
      progress: 82,
      assignments: 4,
      announcements: 3,
      description: 'Hands-on physics experiments and analysis',
      semester: 'Fall 2024',
      credits: 2
    }
  ];

  return baseCourses;
};

export const getDashboardStats = (userRole: UserRole) => {
  switch (userRole) {
    case 'admin':
      return [
        { title: 'Total Courses', value: '156', icon: '📚' },
        { title: 'Active Teachers', value: '23', icon: '👨‍🏫' },
        { title: 'Total Students', value: '1,247', icon: '👥' },
        { title: 'Live Classes', value: '8', icon: '📹' }
      ];
    case 'teacher':
      return [
        { title: 'My Courses', value: '6', icon: '📚' },
        { title: 'Total Students', value: '180', icon: '👥' },
        { title: 'Pending Assignments', value: '12', icon: '📝' },
        { title: 'Live Classes Today', value: '3', icon: '📹' }
      ];
    case 'student':
      return [
        { title: 'Enrolled Courses', value: '8', icon: '📚' },
        { title: 'Assignments Due', value: '4', icon: '📝' },
        { title: 'Average Progress', value: '75%', icon: '📊' },
        { title: 'Live Classes', value: '2', icon: '📹' }
      ];
    default:
      return [];
  }
};
